import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import IconButton from '@mui/material/IconButton';
import Dialog from 'pages/Dashboard/components/Dialog';
import Button from 'pages/Dashboard/components/Button';
import SearchInput from 'pages/Dashboard/components/SearchInput';
import SearchIcon from 'core/icons/search';
import {
  TemplateListSkeleton,
  TemplateOption,
} from 'pages/Dashboard/components/TemplateSelect';
import {
  SectionTemplateContentDTO,
  TemplateListItemDTO,
} from 'dtos';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';
import {
  useGetBulkSectionTemplateContents,
  useSectionTemplateOptions,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import DraggableList from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/DraggableList';
import {
  useAppendToVisitNote,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  joinSectionTemplateContents,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  useNotify,
} from 'core/hooks';
import TabFilter from 'views/EMR/components/TabFilter';
import {
  useFormContext,
} from 'react-hook-form';
import {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';
import TemplatePreviewDialog, {
  TemplatePreviewSection,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/TemplatePreviewDialog';

type Props = {
  onClose: () => void;
};

type SectionDefinition = {
  fieldName: 'additionalHPI' | 'physicalExam' | 'radiology' | 'additionalAssessment' | 'plan';
  label: string;
  sectionName: string;
};

type SelectedTemplate = TemplateListItemDTO & {
  sectionName: string;
  templateName: string;
};

const sections: SectionDefinition[] = [
  {
    fieldName: 'additionalHPI',
    label: 'Subjective',
    sectionName: VisitNoteSOAPSectionNames.AdditionalHPITemplate,
  },
  {
    fieldName: 'physicalExam',
    label: 'Physical Exam',
    sectionName: VisitNoteSOAPSectionNames.PhysicalExamTemplate,
  },
  {
    fieldName: 'radiology',
    label: 'Review of Imaging',
    sectionName: VisitNoteSOAPSectionNames.RadiologyTemplate,
  },
  {
    fieldName: 'additionalAssessment',
    label: 'Additional Assessment',
    sectionName: VisitNoteSOAPSectionNames.AdditionalAssessmentTemplate,
  },
  {
    fieldName: 'plan',
    label: 'Plan',
    sectionName: VisitNoteSOAPSectionNames.PlanTemplate,
  },
];

const allSectionsLabel = 'All';
const sectionTabOptions = [allSectionsLabel, ...sections.map(({ label }) => label)];

function joinContentsBySection(contents: SectionTemplateContentDTO[]) {
  return sections.flatMap((section) => {
    const values = contents
      .filter((template) => template.sectionName === section.sectionName)
      .map(({ content }) => content);
    return values.length > 0
      ? [{ ...section, content: joinSectionTemplateContents(values) }]
      : [];
  });
}

function getTemplateKey(template: Pick<SelectedTemplate, 'sectionName' | 'templateName'>) {
  return `${template.sectionName}:${template.templateName}`;
}

type AvailableTemplateListProps = {
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  sections: Array<SectionDefinition & { templates: SelectedTemplate[] }>;
  selectedKeys: Set<string>;
  onToggle: (template: SelectedTemplate) => void;
};

function LoadMoreTemplates({
  isFetching,
  isError,
  onLoadMore,
}: Pick<AvailableTemplateListProps, 'isFetching' | 'isError' | 'onLoadMore'>) {
  const rowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFetching || isError) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
    });
    observer.observe(rowRef.current!);
    return () => observer.disconnect();
  }, [isFetching, isError, onLoadMore]);

  return (
    <div ref={rowRef} className="p-2 text-center text-xs text-gray-500">
      {isError ? (
        <Button variant="text" disabled={isFetching} onClick={onLoadMore}>
          Load more templates
        </Button>
      ) : (
        <span role="status" aria-label="More templates">
          {isFetching ? 'Loading templates...' : 'Scroll to load more templates'}
        </span>
      )}
    </div>
  );
}

function AvailableTemplateList({
  isLoading,
  isFetching,
  isError,
  hasNextPage,
  onLoadMore,
  sections: availableSections,
  selectedKeys,
  onToggle,
}: AvailableTemplateListProps) {
  const isEmpty = !isLoading && availableSections.every(({ templates }) => templates.length === 0);

  return (
    <div
      role="region"
      aria-label="Available templates"
      className="min-h-0 flex-1 overflow-y-auto"
      style={{ scrollbarGutter: 'stable' }}
    >
      {isLoading && (
        <TemplateListSkeleton />
      )}
      {isEmpty && (
        <div className="flex h-full items-center justify-center text-sm text-gray-500">
          No templates found
        </div>
      )}
      {!isLoading && availableSections.filter(({ templates }) => templates.length > 0).map(({ label, sectionName, templates }) => (
        <div key={sectionName}>
          <h4 className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700">
            {label}
          </h4>
          {templates.map((template) => {
            const templateKey = getTemplateKey(template);
            const isSelected = selectedKeys.has(templateKey);
            return (
              <TemplateOption
                key={templateKey}
                id={`bulk-${templateKey}`}
                template={template}
                selected={isSelected}
                showCheckbox
                flat
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
                onClick={() => { onToggle(template); }}
                onKeyDown={(event) => {
                  if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    event.currentTarget.click();
                  }
                }}
              />
            );
          })}
        </div>
      ))}
      {!isLoading && hasNextPage && (
        <LoadMoreTemplates isFetching={isFetching} isError={isError} onLoadMore={onLoadMore} />
      )}
    </div>
  );
}

export default function BulkSectionTemplateDialog({ onClose }: Props) {
  const notify = useNotify();
  const { getValues } = useFormContext<VisitNoteData>();
  const { appendTextField } = useAppendToVisitNote();
  const {
    mutateAsync: getTemplateContents,
    isPending: isLoadingTemplateContents,
  } = useGetBulkSectionTemplateContents();
  const [searchText, setSearchText] = useState('');
  const [activeSection, setActiveSection] = useState<string>(allSectionsLabel);
  const activeSectionName = sections.find(({ label }) => label === activeSection)?.sectionName;
  const {
    data,
    isPending: isLoading,
    isFetching,
    isError,
    hasNextPage,
    fetchNextPage,
  } = useSectionTemplateOptions(activeSectionName, searchText);
  const loadMoreTemplates = useCallback(() => {
    fetchNextPage({ cancelRefetch: false });
  }, [fetchNextPage]);
  const [isAddingTemplates, setIsAddingTemplates] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<SelectedTemplate[]>([]);
  const [previewContents, setPreviewContents] = useState<
    ReturnType<typeof joinContentsBySection> | null
  >(null);

  const visibleSections = useMemo(() => sections.map((section) => ({
    ...section,
    templates: (data?.data ?? []).filter(({ sectionName }) => sectionName === section.sectionName),
  })), [data]);

  const selectedKeys = useMemo(
    () => new Set(selectedTemplates.map(getTemplateKey)),
    [selectedTemplates],
  );

  const toggleTemplate = (template: SelectedTemplate) => {
    const key = getTemplateKey(template);
    setSelectedTemplates((current) => (
      selectedKeys.has(key)
        ? current.filter((item) => getTemplateKey(item) !== key)
        : [...current, template]
    ));
  };

  const updateSectionOrder = (sectionName: string, orderedTemplates: SelectedTemplate[]) => {
    let nextIndex = 0;
    setSelectedTemplates((current) => current.map((template) => {
      if (template.sectionName !== sectionName) return template;
      const orderedTemplate = orderedTemplates[nextIndex];
      nextIndex += 1;
      return orderedTemplate;
    }));
  };

  const removeTemplate = (template: SelectedTemplate) => {
    const key = getTemplateKey(template);
    setSelectedTemplates((current) => (
      current.filter((item) => getTemplateKey(item) !== key)
    ));
  };

  const applyTemplateContents = (contents: ReturnType<typeof joinContentsBySection>) => {
    contents.forEach(({ fieldName, content }) => {
      appendTextField(fieldName, content);
    });

    notify({
      message: `${selectedTemplates.length} section template${selectedTemplates.length === 1 ? '' : 's'} added`,
      variant: 'success',
    });
    onClose();
  };

  const loadTemplateContents = () => getTemplateContents(selectedTemplates.map((template) => ({
    sectionName: template.sectionName,
    templateName: template.templateName,
  })));

  const applyTemplates = async () => {
    setIsAddingTemplates(true);
    try {
      applyTemplateContents(joinContentsBySection(await loadTemplateContents()));
    } catch {
      // API errors are displayed by the global error handler.
    } finally {
      setIsAddingTemplates(false);
    }
  };

  const previewTemplates = async () => {
    try {
      setPreviewContents(joinContentsBySection(await loadTemplateContents()));
    } catch {
      // API errors are displayed by the global error handler.
    }
  };

  const [additionalHPI, physicalExam, radiology, additionalAssessment, plan] = getValues([
    'additionalHPI', 'physicalExam', 'radiology', 'additionalAssessment', 'plan',
  ]);
  const previewSections: TemplatePreviewSection[] = useMemo(() => sections.map(({
    fieldName,
    label,
    sectionName,
  }) => ({
    key: sectionName,
    label,
    currentContent: { additionalHPI, physicalExam, radiology, additionalAssessment, plan }[fieldName],
    templateContent: previewContents?.find((template) => template.sectionName === sectionName)?.content,
  })), [additionalHPI, physicalExam, radiology, additionalAssessment, plan, previewContents]);

  const addButtonText = isAddingTemplates
    ? 'Adding Templates...'
    : `Add ${selectedTemplates.length} Template${selectedTemplates.length === 1 ? '' : 's'}`;
  const searchPlaceholder = activeSection === allSectionsLabel
    ? 'Search all templates'
    : `Search ${activeSection} templates`;

  return (
    <Dialog
      open
      title="Use Section Templates"
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      className="!h-[38rem] max-h-[88vh] !w-full !space-y-0 gap-2 !overflow-hidden"
      classes={{ bodyRoot: '!max-w-[62rem] !overflow-hidden' }}
    >
      <TabFilter
        options={sectionTabOptions}
        defaultTab={allSectionsLabel}
        variant="minimal"
        className="w-full shrink-0"
        onUpdate={setActiveSection}
      />
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-4 overflow-hidden">
        <section className="flex min-h-0 flex-col gap-2">
          <h3 className="text-sm font-semibold">Available templates</h3>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200">
            <div className="border-b border-gray-200 p-2">
              <SearchInput
                id="bulk-section-template-search"
                value={searchText}
                placeholder={searchPlaceholder}
                icon={<SearchIcon className="!ml-3 !h-4 !w-4 text-gray-400" />}
                onChange={setSearchText}
              />
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              {isError && (
                <div role="alert" className="p-3 text-xs text-red-600">
                  Could not load templates.
                </div>
              )}
              {(!isError || (data?.data.length ?? 0) > 0) && (
                <AvailableTemplateList
                  key={`${activeSection}:${searchText.trim()}`}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  isError={isError}
                  hasNextPage={hasNextPage}
                  onLoadMore={loadMoreTemplates}
                  sections={visibleSections}
                  selectedKeys={selectedKeys}
                  onToggle={toggleTemplate}
                />
              )}
            </div>
          </div>
        </section>

        <section className="flex min-h-0 flex-col gap-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Template order</h3>
              <p className="mt-0.5 text-xs text-gray-500">
                Drag templates into the order they should appear in each section.
              </p>
            </div>
            {selectedTemplates.length > 0 && (
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs text-gray-500">
                  {selectedTemplates.length}
                  {' '}
                  selected
                </span>
                <Button
                  variant="text"
                  color="inherit"
                  className="!min-w-0 !rounded !px-2 !py-0.5 !text-xs !font-medium !text-gray-600 hover:!bg-gray-100 hover:!text-gray-900"
                  onClick={() => { setSelectedTemplates([]); }}
                >
                  Clear All
                </Button>
              </div>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
            {selectedTemplates.length === 0 ? (
              <div className="flex h-full items-center justify-center px-8 text-center text-sm text-gray-500">
                Selected templates will appear here
              </div>
            ) : sections.map(({ label, sectionName }) => {
              const sectionTemplates = selectedTemplates.filter(
                (template) => template.sectionName === sectionName,
              );
              if (sectionTemplates.length === 0) return null;

              return (
                <div key={sectionName} className="pb-2 last:pb-0">
                  <h4 className="sticky top-0 z-10 border-b border-gray-200 bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700">
                    {label}
                  </h4>
                  <div className="px-2">
                    <DraggableList
                      items={sectionTemplates}
                      getValue={getTemplateKey}
                      itemSpacing={8}
                      onChange={(items) => { updateSectionOrder(sectionName, items); }}
                      renderOption={(template) => (
                        <div className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5 shadow-sm">
                          <DragIndicatorIcon className="!h-5 !w-5 shrink-0 text-gray-400" />
                          <span className="min-w-0 flex-1 truncate text-xs">
                            {template.templateName}
                          </span>
                          <IconButton
                            aria-label={`Remove ${template.templateName} from ${label}`}
                            className="!-my-1 !p-1"
                            size="small"
                            onClick={() => { removeTemplate(template); }}
                          >
                            <CloseIcon className="!h-4 !w-4 text-gray-500" />
                          </IconButton>
                        </div>
                      )}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
      <div className="flex shrink-0 justify-between gap-2">
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <div className="flex gap-2">
          <Button
            variant="outlined"
            disabled={selectedTemplates.length === 0 || isLoadingTemplateContents}
            onClick={previewTemplates}
          >
            Preview
          </Button>
          <Button
            color="primary"
            disabled={selectedTemplates.length === 0 || isLoadingTemplateContents}
            onClick={applyTemplates}
          >
            {addButtonText}
          </Button>
        </div>
      </div>
      {previewContents && (
        <TemplatePreviewDialog
          sections={previewSections}
          isApplying={isAddingTemplates}
          applyLabel={addButtonText}
          onClose={() => { setPreviewContents(null); }}
          onApply={() => { applyTemplateContents(previewContents); }}
        />
      )}
    </Dialog>
  );
}
